import { OverlayProvider } from "@/components/modal/OverlayProvider";
import { ChalkAssistantWorkspace } from "@/components/agent-conversation/ChalkAssistantWorkspace";
import { CustomToaster } from "@/components/CustomToaster";
import { RouteLoadingIndicator } from "@/components/loaders/RouteLoadingIndicator";
import { ErrorMessage } from "@/components/Message";
import { ChalkEmployeeGuard } from "@/components/admin/ChalkEmployeeGuard";
import { DemoModeHotkey } from "@/components/admin/DemoModeHotkey";
import { InlineCommand } from "@/components/code/InlineCommand";
import {
  GlobalAppStateListener,
  GlobalAppStateWrapper,
} from "@/components/context-providers/GlobalAppStateWrapper";
import { GlobalIdleWrapper } from "@/components/context-providers/GlobalIdleWrapper";
import { NextEnvVarsWrapper } from "@/components/context-providers/NextEnvVarsWrapper";
import { RouteInfoProvider } from "@/components/context-providers/RouteInfoProvider";
import {
  ChalkGlobalErrorComponent,
  ChalkSimpleErrorComponent,
} from "@/components/layout/ErrorBoundary";
import { ExtremelySimpleLayout } from "@/components/layout/Layout";
import {
  NetworkingWrapper,
  UnauthenticatedNetworkingWrapper,
} from "@/components/layout/NetworkingWrapper";
import DatadogInit from "@/utils/datadog-init";
import { installDatadogNavigationInstrumentation } from "@/utils/datadog-navigation";
import { NextEnvVarProps } from "@/utils/getServerSideProps";
import { NextEnvVars, getNextEnvVarFallbacks } from "@/utils/hooks/get-next-env-vars";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useScopeGeneration } from "@/utils/hooks/use-scope-generation";
import { installHistoryEntryIndexTracking } from "@/utils/history-entry-index";
import {
  pathRequiresAuthenticationAndTeamMembership,
  pathRequiresNetworkingClient,
} from "@/utils/networking-constants";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { Session } from "next-auth";
import { SessionProvider } from "next-auth/react";
import { MarketingThemeProvider } from "@/components/marketing-components/MarketingThemeProvider";
import { AppProps } from "next/app";
import { ErrorBoundary } from "next/dist/client/components/error-boundary";
import { useEffect, useMemo } from "react";
import ModalContainer from "react-modal-promise";
import "../styles/global.css";

// eslint-disable-next-line eslint-js/no-restricted-syntax
if (process.env.NODE_ENV !== "production") {
  // sketchy hack in dev mode to prevent react 19 from crashing when doing props comparison
  // for components that take bigints as props; react 19 uses JSON.stringify and that breaks on
  // bigints, so we need to patch the toJSON in order to dodge this issue.
  (BigInt.prototype as any).toJSON = function () {
    return this.toString();
  };
}

export interface ChalkAppProps extends Partial<NextEnvVarProps> {
  session?: Session | null;
}

export default function ChalkApp({ Component, router, pageProps }: AppProps<ChalkAppProps>) {
  return (
    <OverlayProvider>
      <RouteInfoProvider>
        <ChalkAppContents Component={Component} router={router} pageProps={pageProps} />
      </RouteInfoProvider>
    </OverlayProvider>
  );
}

function ChalkAppContents({ Component, router, pageProps }: AppProps<ChalkAppProps>) {
  const { envVars: serverSideEnvVars, session } = pageProps;
  const { projectId, environmentId } = useRouteInfo();
  // Remounts the page on a project or environment switch, but not when the ids first resolve
  // after hydration.
  const pageKey = useScopeGeneration([projectId, environmentId]);
  useEffect(() => installHistoryEntryIndexTracking(), []);

  const envVars: NextEnvVars = useMemo(() => {
    // eslint-disable-next-line eslint-js/no-restricted-syntax
    if (process.env.DEVELOPMENT_MODE) {
      // Do not delete! Returning empty env vars here forces an error
      // if you forget to add getServerSideProps to page components that need it!
      return serverSideEnvVars ?? ({} as NextEnvVars);
    } else {
      return getNextEnvVarFallbacks(serverSideEnvVars);
    }
  }, [serverSideEnvVars]);

  const rawPath = useMemo(
    () => new URL(router.asPath, "https://chalk.ai").pathname,
    [router.asPath],
  );

  // When Next renders the 404 or _error page, `router.asPath` still holds the originally
  // requested URL (e.g. a /cli or /projects path that doesn't exist in this build), so the
  // prefix check below would demand networking env vars that the statically-rendered error
  // pages never receive — masking the 404 behind a "Configuration Error" box on self-hosted
  // deployments. Match on the resolved route to let real error pages render as themselves.
  const isErrorPage = router.pathname === "/404" || router.pathname === "/_error";

  const pathRequiresNetworking = useMemo(() => {
    return !isErrorPage && pathRequiresNetworkingClient(rawPath);
  }, [rawPath, isErrorPage]);
  // The sign-up flow is designed for light only (white form beside the green art panel), even though these routes
  // need networking like the dashboard does.
  const pathIsSignupFlow =
    rawPath === "/signup" || rawPath.startsWith("/join/") || rawPath.startsWith("/onboarding/");
  const pathAllowsThemeToggle =
    (pathRequiresNetworking && !pathIsSignupFlow) ||
    rawPath.startsWith("/test-components") ||
    rawPath.startsWith("/marketing-components");

  useEffect(() => installDatadogNavigationInstrumentation(router), [router]);

  const pathRequiresTeam = useMemo(() => {
    return pathRequiresAuthenticationAndTeamMembership(rawPath);
  }, [rawPath]);

  useEffect(() => {
    document.body.classList.toggle("design-system-typography-portals", pathRequiresNetworking);

    return () => {
      document.body.classList.remove("design-system-typography-portals");
    };
  }, [pathRequiresNetworking]);

  return (
    <SessionProvider session={session}>
      <MarketingThemeProvider
        embed={rawPath.startsWith("/marketing-components/embed")}
        attribute="class"
        storageKey={
          rawPath.startsWith("/marketing-components/embed") ? "chalk-docs-embed-theme" : "theme"
        }
        defaultTheme={pathRequiresNetworking ? "dark" : "light"}
        forcedTheme={pathAllowsThemeToggle ? undefined : "light"}
        disableTransitionOnChange
      >
        <RouteLoadingIndicator />
        <DatadogInit dopplerEnvironment={envVars.DOPPLER_ENVIRONMENT ?? undefined} />
        <SpeedInsights />
        <GlobalAppStateWrapper>
          <GlobalAppStateListener />
          <DemoModeHotkey />
          <GlobalIdleWrapper>
            <NextEnvVarsWrapper envVars={envVars}>
              {pathRequiresNetworking ? (
                envVars.CHALK_GRPC_API_HOST ? (
                  pathRequiresTeam ? (
                    // eslint-disable-next-line eslint-js/no-restricted-syntax
                    <NetworkingWrapper grpcApiHost={envVars.CHALK_GRPC_API_HOST}>
                      <ErrorBoundary errorComponent={ChalkGlobalErrorComponent}>
                        <ChalkAssistantWorkspace>
                          <Component {...pageProps} envVars={envVars} key={pageKey} />
                        </ChalkAssistantWorkspace>
                        <CustomToaster />
                        {/* isAppendIntances (the library's spelling) renders new instances last.
                            The default prepends, painting a modal opened from inside another
                            modal under its opener whenever the two share a z-index. */}
                        <ModalContainer isAppendIntances />
                      </ErrorBoundary>
                    </NetworkingWrapper>
                  ) : (
                    // eslint-disable-next-line eslint-js/no-restricted-syntax
                    <UnauthenticatedNetworkingWrapper grpcApiHost={envVars.CHALK_GRPC_API_HOST}>
                      {/* Not ChalkGlobalErrorComponent: its Layout needs the team
                          providers this wrapper deliberately does not mount. */}
                      <ErrorBoundary errorComponent={ChalkSimpleErrorComponent}>
                        <Component {...pageProps} envVars={envVars} key={pageKey} />
                        <CustomToaster />
                        <ModalContainer isAppendIntances />
                      </ErrorBoundary>
                    </UnauthenticatedNetworkingWrapper>
                  )
                ) : (
                  <ExtremelySimpleLayout>
                    <ErrorMessage title={"Configuration Error"}>
                      <div>Missing environment variable CHALK_GRPC_API_HOST.</div>
                      <ChalkEmployeeGuard>
                        <div>
                          Please add{" "}
                          <InlineCommand>
                            export const getServerSideProps = getServerSidePropsUtil;
                          </InlineCommand>{" "}
                          to the page component.
                        </div>
                      </ChalkEmployeeGuard>
                    </ErrorMessage>
                  </ExtremelySimpleLayout>
                )
              ) : (
                <ErrorBoundary errorComponent={ChalkSimpleErrorComponent}>
                  <Component {...pageProps} envVars={envVars} key={pageKey} />
                  <CustomToaster />
                  <ModalContainer isAppendIntances />
                </ErrorBoundary>
              )}
            </NextEnvVarsWrapper>
          </GlobalIdleWrapper>
        </GlobalAppStateWrapper>
      </MarketingThemeProvider>
    </SessionProvider>
  );
}
